import { inject, Injectable, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, combineLatest, map, switchMap } from 'rxjs';

/**
 * Title strategy that treats route `title` values as Transloco keys.
 *
 * Routes declare `title: 'pageTitle.devices'` and this strategy renders it as
 * "Devices - Papercast" (via the `app.titleTemplate` key). Built on
 * selectTranslate streams rather than translate() so the title is set only
 * once the translation file is loaded, and re-set when the user switches
 * language. Routes without a title fall back to the bare app name.
 */
@Injectable({ providedIn: 'root' })
export class TranslatedTitleStrategy extends TitleStrategy {
    private readonly transloco = inject(TranslocoService);
    private readonly title = inject(Title);
    private readonly titleKey$ = new BehaviorSubject<string | null>(null);

    /**
     * The active route's title key, for in-app chrome (the topbar renders the
     * page title from it). Same source as the document title, so the two can
     * never disagree.
     */
    readonly pageTitleKey = signal<string | null>(null);

    constructor() {
        super();
        this.titleKey$
            .pipe(
                switchMap(key => {
                    const appName$ = this.transloco.selectTranslate<string>('app.name');
                    if (!key) {
                        return appName$;
                    }
                    return combineLatest([this.transloco.selectTranslate<string>(key), appName$]).pipe(
                        map(([page, appName]) =>
                            this.transloco.translate('app.titleTemplate', { page, appName })
                        )
                    );
                }),
                takeUntilDestroyed()
            )
            .subscribe(title => this.title.setTitle(title));
    }

    override updateTitle(snapshot: RouterStateSnapshot): void {
        const key = this.buildTitle(snapshot) ?? null;
        this.titleKey$.next(key);
        this.pageTitleKey.set(key);
    }
}
